/* eslint-disable arrow-body-style */
import { useNavigate } from 'react-router-dom';
import { Box, Rating, Typography, Stack, MenuItem,Chip } from '@mui/material';
// @types
// components
import Image from '../../../../components/Image';
import TextMaxLine from '../../../../components/TextMaxLine';
import { StyledCardContainer, StyledCardContent } from '../styles';
import { TableMoreMenu } from 'src/components/table';
import { useState } from 'react';
import Iconify from 'src/components/Iconify';
import { Course } from 'src/@types/Course';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useCanReviewPartners } from 'src/hooks/usePartnerOps';

// A partner-made course: its lessons and details are managed in Partner courses
// (admin / super-admin only), so the grid never opens the Kayfa editors for it.
export const isPartnerCourse = (course: Course) =>
  Boolean(course?.partnerOwned ?? course?.partner);
export const PARTNER_CHIP_TEXT = 'Partner course';
export const PARTNER_ADMIN_ONLY_CHIP_TEXT = 'Managed in Partner courses (admin only)';

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

type Props = {
  course: Course;
  index?: number;
  handleEditRow(): void;
  handleDeleteRow(): void;
};

export default function CourseCard({ course, index, handleDeleteRow, handleEditRow }: Props) {
  const canOpenPartnerPages = useCanReviewPartners();
  const partner = isPartnerCourse(course);

  return (
    <StyledCardContainer
      onClick={(e) => {
         e.stopPropagation();
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Box
          style={{
            position: 'absolute',
            top: '10%',
            left: '2%',
            borderRadius: '6px',
            background: 'rgba(246, 228, 255)',
            paddingLeft: '15px',
            paddingRight: '15px',
            color: '#BF5AF2',
          }}
        >
          {course?.category?.title}
        </Box>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ position: 'absolute', top: '10%', right: '2%', zIndex: 1 }}
        >
          {partner && (
            <Chip
              label={canOpenPartnerPages ? PARTNER_CHIP_TEXT : PARTNER_ADMIN_ONLY_CHIP_TEXT}
              size="small"
              color="info"
              sx={{ fontWeight: 600 }}
              data-testid="course-card-partner"
            />
          )}
          {course?.active === false && (
            <Chip label="Draft" size="small" color="warning" sx={{ fontWeight: 600 }} />
          )}
        </Stack>

        <Image alt="cover" src={course.briefVideo} ratio="4/3" />
      </Box>

      <CourseContent
        course={course}
        handleDeleteRow={handleDeleteRow}
        handleEditRow={handleEditRow}
      />
    </StyledCardContainer>
  );
}

// ----------------------------------------------------------------------

type CourseContentProps = {
  course: Course;
  handleEditRow(): void;
  handleDeleteRow(): void;
};

function CourseContent({ course, handleEditRow, handleDeleteRow }: CourseContentProps) {
  return (
    <StyledCardContent>
      <Stack>
        <Stack
          direction={'row'}
          width={'100%'}
          alignItems={'center'}
          mb={'10px'}
          justifyContent={'space-between'}
        >
          <TextMaxLine
            sx={{ width: 'auto !important', fontSize: '18px !important', fontWeight: '700' }}
          >
            {course.name}
          </TextMaxLine>
          <ToolBar onEditRow={handleEditRow} onDeleteRow={handleDeleteRow} row={course} />
        </Stack>
        <Stack direction={'row'} justifyContent={'space-between'} mb={'20px'} alignItems={'center'}>
          <Typography style={{ fontSize: '14px' }} component="div" color={'GrayText'}>
            {course?.track?.title}
          </Typography>
          <Typography style={{ fontSize: '14px' }} component="div" color={'GrayText'}>
            {course?.instructor?.name}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          component="div"
          color={'GrayText'}
          mb={'10px'}
          data-testid="course-card-order"
        >
          {typeof course?.order === 'number' ? `Order ${course.order}` : 'Order —'}
        </Typography>
        <Typography
          gutterBottom
          variant="caption"
          style={{ overflow: 'auto', textOverflow: 'ellipsis', height: '75px' }}
          component="div"
          color={'GrayText'}
        >
          {course.description}
        </Typography>
        <Stack
          justifyContent={'space-between'}
          alignItems={'center'}
          width={'100%'}
          flexDirection={'row'}
        >
          <Typography
            gutterBottom
            variant="caption"
            mb={'0 !important'}
            component="div"
            color={'GrayText'}
          >
            {course.duration > 60 ? (course.duration / 60).toFixed(0) + ' H ' : ''}
            {course.duration % 60} M
          </Typography>
          <Rating name="read-only" value={course.rating} readOnly />
        </Stack>
        <Stack direction={'row'} flexWrap={'wrap'} spacing={1}>
          {course?.keywords.map((item, index) => {
            return (
              <Chip sx={{ marginTop: '5px !important' }} key={item + `${index}`} label={item} />
            );
          })}
        </Stack>
      </Stack>
    </StyledCardContent>
  );
}

export const ToolBar = ({
  onEditRow,
  row,
  onDeleteRow,
}: {
  row: Course;
  onEditRow(): void;
  onDeleteRow(): void;
}) => {
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const canOpenPartnerPages = useCanReviewPartners();
  const partner = isPartnerCourse(row);
  // Partner course: Content / Edit open its partner page (admin only); no Delete.
  const partnerPage = () => navigate(PATH_DASHBOARD.partners.courseDetail(row.id || row._id));

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  return (
    <TableMoreMenu
      width={220}
      open={openMenu}
      onOpen={(e) => {
        e.stopPropagation();
        handleOpenMenu(e);
      }}
      onClose={handleCloseMenu}
      actions={
        <>
          <MenuItem
            onClick={() => {
              window.open(`https://kayfa.io/courses/${row.id}`, '_blank');
            }}
          >
            <Iconify icon={'eva:eye-fill'} />
            Open On User Portal
          </MenuItem>
          {(!partner || canOpenPartnerPages) && (
            <MenuItem
              onClick={() => {
                if (partner) partnerPage();
                else navigate(PATH_DASHBOARD.courses.view(row.id));
              }}
            >
              <Iconify icon={'eva:eye-fill'} />
              Content
            </MenuItem>
          )}
          {(!partner || canOpenPartnerPages) && (
            <MenuItem
              onClick={() => {
                if (partner) partnerPage();
                else onEditRow();
                handleCloseMenu();
              }}
            >
              <Iconify icon={'eva:edit-fill'} />
              Edit
            </MenuItem>
          )}
          {!partner && (
            <MenuItem
              onClick={() => {
                onDeleteRow();
                handleCloseMenu();
              }}
              sx={{ color: 'error.main' }}
            >
              <Iconify icon={'eva:trash-2-outline'} />
              Delete
            </MenuItem>
          )}
        </>
      }
    />
  );
};
